import { useState, useRef, useEffect, useMemo } from "react";
import { Box, Typography, TextField, IconButton, Chip } from "@mui/material";
import { Send, ArrowBack, Check } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import useMessagesAPIClient from "../../api/messages/hooks/useMessagesAPIClient";
import { markMessageAsRead, setMessages } from "../../redux/actions/messages";

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
};

const MessageBubble = ({ message, isFromAdmin }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isFromAdmin ? "flex-start" : "flex-end",
        mb: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: "70%",
          bgcolor: isFromAdmin ? "#197b68" : "#f5f5f5",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        }}
      >
        {message.subject && (
          <Box
            sx={{
              bgcolor: isFromAdmin ? "#004a3c" : "#e0e0e0",
              px: 2,
              py: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: "bold",
                mb: "0 !important",
                color: isFromAdmin ? "white !important" : "text.primary",
              }}
            >
              {message.subject}
            </Typography>
          </Box>
        )}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              mb: "0 !important",
              color: isFromAdmin ? "white !important" : "text.primary",
            }}
          >
            {message.message}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isFromAdmin
                  ? "rgba(255,255,255,0.7) !important"
                  : "text.secondary",
                mb: "0 !important",
              }}
            >
              {formatTime(message.sentAt)}
            </Typography>
            {!isFromAdmin && (
              <Check sx={{ fontSize: 14, color: "text.secondary" }} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ConversationThread = ({ goBack }) => {
  const MAX_MESSAGE_LENGTH = 2000;
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const hasMarkedAsRead = useRef(false);
  const messages = useSelector((state) => state.messages);
  const {
    sendReply,
    markMessageAsRead: markAsReadAPI,
    getMessages,
  } = useMessagesAPIClient();
  const dispatch = useDispatch();

  // Filter to only direct messages (not global)
  const directMessages = useMemo(() => {
    return messages
      .filter((msg) => msg.sentTo !== "global")
      .sort((a, b) => a.sentAt - b.sentAt);
  }, [messages]);

  // Mark unread direct messages as read when component mounts and messages are available
  useEffect(() => {
    // Only run once when we have messages
    if (hasMarkedAsRead.current || directMessages.length === 0) return;

    const markUnreadAsRead = async () => {
      // Get unread messages from admin (senderType is "admin" or undefined/null for older messages)
      const unreadMessages = directMessages.filter(
        (msg) => !msg.read && msg.senderType !== "user",
      );

      if (unreadMessages.length > 0) {
        hasMarkedAsRead.current = true;
        for (const msg of unreadMessages) {
          dispatch(markMessageAsRead(msg.messageId));
          await markAsReadAPI(msg.messageId);
        }
      } else {
        // No unread messages, but mark as processed anyway
        hasMarkedAsRead.current = true;
      }
    };

    markUnreadAsRead();
  }, [directMessages, dispatch, markAsReadAPI]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      const messagesData = await getMessages();
      if (messagesData) {
        dispatch(setMessages(messagesData));
      }
    }, 5000);

    return () => {
      clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [directMessages]);

  // Group messages by date
  const groupedMessages = directMessages.reduce((groups, message) => {
    const date = formatDate(message.sentAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const handleSendReply = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await sendReply(newMessage.trim());
      setNewMessage("");
      // Small delay to ensure backend has processed the message
      await new Promise((resolve) => setTimeout(resolve, 100));
      // Refresh messages to get the sent message
      const messagesData = await getMessages();
      if (messagesData) {
        dispatch(setMessages(messagesData));
      }
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      className="messages_view"
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          bgcolor: "#004a3c",
          color: "white",
          borderRadius: "8px 8px 0 0",
        }}
      >
        {goBack && (
          <IconButton onClick={goBack} sx={{ color: "white !important" }}>
            <ArrowBack />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mb: "0 !important", color: "white !important" }}
          >
            Mindstack Support
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.8, mb: "0 !important", color: "white !important" }}
          >
            Direct messages with the Mindstack team
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          bgcolor: "#fafafa",
        }}
        className="scroller"
      >
        {directMessages.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No messages yet
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <Box key={date}>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip
                  label={date}
                  size="small"
                  sx={{ bgcolor: "white", fontSize: "0.75rem" }}
                />
              </Box>
              {msgs.map((message) => (
                <MessageBubble
                  key={message.messageId}
                  message={message}
                  isFromAdmin={
                    message.senderType === "admin" || !message.senderType
                  }
                />
              ))}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          p: 2,
          bgcolor: "white",
          borderTop: "1px solid",
          borderColor: "divider",
          borderRadius: "0 0 8px 8px",
        }}
      >
        <TextField
          fullWidth
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => {
            if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
              setNewMessage(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendReply();
            }
          }}
          multiline
          maxRows={3}
          size="small"
          disabled={sending}
          inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
          helperText={
            newMessage.length > 1800
              ? `${newMessage.length}/${MAX_MESSAGE_LENGTH}`
              : ""
          }
        />
        <IconButton
          color="primary"
          onClick={handleSendReply}
          disabled={!newMessage.trim() || sending}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            bgcolor: "#197b68",
            color: "white !important",
            "&:hover": { bgcolor: "#004a3c" },
            "&:disabled": { bgcolor: "grey.300", color: "grey.500 !important" },
            "& svg": { color: "white !important" },
          }}
        >
          <Send sx={{ color: "white !important" }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ConversationThread;
